import { useMemo } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { DynamicEquationEditor, MathSymbols } from '@/src/lib';
import { Constants, translate } from '@/src/utils';
import { ErrorMessage } from '@/src/ui';

interface ExamNotepadProps {
    open: boolean;
    onClose: () => void;
    content: string;
    onChange: (content: string) => void;
    language: 'en' | 'es';
    disabled?: boolean;
    error?: string;
}

const EXPRESSION_KEYS = ['frac', 'sqrt', 'nrt', 'exp', 'seg', 'line', 'ray'];

export default function ExamNotepad({
    open,
    onClose,
    content,
    onChange,
    language,
    disabled = false,
    error,
}: ExamNotepadProps) {
    const notepadAllowedKeys = useMemo(
        () => [
            ...Object.keys(MathSymbols.numbers),
            ...Object.keys(MathSymbols.symbols),
            ...EXPRESSION_KEYS,
            'free',
            'spc',
        ],
        []
    );

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    p: 4,
                    bgcolor: 'background.paper',
                    margin: 'auto',
                    mt: 8,
                    width: 700,
                    maxWidth: '95vw',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    borderRadius: 2,
                }}
            >
                <h2>{translate(language, 'notepad')}</h2>

                <Box sx={{ mt: 2, flex: 1 }}>
                    <DynamicEquationEditor
                        contentKey="exam-notepad"
                        initialValue={content || Constants.DEE_DEFAULT_TEXT}
                        setOutput={(output: string) => {
                            onChange(output);
                        }}
                        showKeypad={true}
                        allowedKeys={notepadAllowedKeys}
                        style={{ width: '100%' }}
                        disabled={disabled}
                    />
                    {error && <ErrorMessage error={error} />}
                    {/* LIMIT 5000 because quotes get escaped & db limit is 3000 */}
                    {content.length > 5000 && (
                        <ErrorMessage
                            color="blue"
                            error="See an error? Your answer may be too long."
                        />
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button variant="outlined" onClick={onClose}>
                        {translate(language, 'close')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
